import { DeleteOutlined, SearchOutlined } from '@ant-design/icons';
import {
  Button,
  Checkbox,
  Empty,
  Form,
  Input,
  List,
  Modal,
  Tooltip,
  Typography,
} from 'antd';
import { useEffect, useRef, useState } from 'react';

import {
  CloseIconsForModalWithDataTestId,
  EmptyComponentWithDataTestId,
} from '@app/components';
import type { NLeavePageProvider } from '@app/providers/LeavePageProvider/LeavePageProvider.types';
import { generateSpanWithDataTestId } from '@app/shared/utils';
import type { NBranches } from '@app/types/Branches';

import { useSetBranchesComponent } from './hooks/useSetBranchesComponent';

import type { CheckboxChangeEvent } from 'antd/es/checkbox';
import type { FormInstance } from 'antd/es/form';
import type { FC } from 'react';

import '@app/components/SetBranchesComponent/styles/SetBranchesComponent.mobile.scss';

export const SetBranchesComponent: FC<NBranches.ModalFormProps> = ({
  open,
  onCancel,
  selectedBranches,
  setSelectedBranches,
  allBranches,
  serverError,
  showUserModal,
  infoData,
  setInfoData,
  dataKey,
  setNewData,
  newDataFieldValue,
  error,
  checkBranchSelection,
  required,
}) => {
  const [checkedBranches, setCheckedBranches] =
    useState<NBranches.ModelBranchItem[]>(selectedBranches);
  const {
    searchValue,
    filteredBranches,
    isSearchApplied,
    searchChangeHandler,
    resetSearch,
  } = useSetBranchesComponent(allBranches);

  const [form] = Form.useForm();

  useEffect(() => {
    setCheckedBranches(selectedBranches);
  }, [selectedBranches]);

  const useResetFormOnCloseModal = ({
    form,
    open,
  }: {
    form: FormInstance;
    open: boolean;
  }) => {
    const prevOpenRef = useRef<boolean>();
    useEffect(() => {
      prevOpenRef.current = open;
    }, [open]);
    const prevOpen = prevOpenRef.current;

    useEffect(() => {
      if (!open && prevOpen) {
        setCheckedBranches(selectedBranches);
        resetSearch();
        form.resetFields();
      }
    }, [form, prevOpen, open]);
  };

  useResetFormOnCloseModal({
    form,
    open,
  });

  const onOk = () => {
    setSelectedBranches(checkedBranches);
    setNewData &&
      newDataFieldValue &&
      setNewData((prev: NLeavePageProvider.Data) => {
        return {
          ...prev,
          [newDataFieldValue]: checkedBranches.length
            ? checkedBranches.map((item) => item.Id)
            : [],
        };
      });
    form.submit();
    onCancel();
    checkBranchSelection?.(checkedBranches);
    if (dataKey && infoData && setInfoData) {
      const newValue = {
        ...infoData[dataKey],
        userBranchIds:
          checkedBranches.length > 0
            ? checkedBranches.map((branch) => branch.Id)
            : [],
      };

      setInfoData({
        ...infoData,
        [dataKey]: newValue,
      });
    }
  };

  const onChange = (
    e: CheckboxChangeEvent,
    branch: NBranches.ModelBranchItem
  ) => {
    if (e.target.checked) {
      setCheckedBranches([...checkedBranches, branch]);
    } else {
      setCheckedBranches(
        checkedBranches.filter(({ Id: localId }) => localId !== branch.Id)
      );
    }
  };

  const removeSelectedBranch = (id: number | undefined) => {
    const newSelectedBranches = selectedBranches.filter(
      ({ Id: localId }) => localId !== id
    );
    setSelectedBranches(newSelectedBranches);
    checkBranchSelection?.(newSelectedBranches);
    setNewData &&
      newDataFieldValue &&
      setNewData((prev: NLeavePageProvider.Data) => {
        return {
          ...prev,
          [newDataFieldValue]: newSelectedBranches.length
            ? newSelectedBranches.map(({ Id }) => Id)
            : [],
        };
      });
    if (dataKey && infoData && setInfoData) {
      const newValue = {
        ...infoData[dataKey],
        userBranchIds:
          newSelectedBranches.length > 0
            ? newSelectedBranches.map(({ Id }) => Id)
            : [],
      };

      setInfoData({
        ...infoData,
        [dataKey]: newValue,
      });
    }
  };

  return (
    <>
      <div className="branches-selection-container">
        <Form.Item
          shouldUpdate={(prevValues, curValues) => prevValues !== curValues}
        >
          {selectedBranches.length ? (
            <List
              className="list-container"
              itemLayout="horizontal"
              dataSource={selectedBranches}
              renderItem={(selectedBranch) => (
                <List.Item
                  data-testid="added-chosen-branch-item"
                  actions={[
                    <Tooltip key="delete" title="Удалить филиал">
                      <DeleteOutlined
                        className="branch-delete"
                        style={{ fontSize: '20px' }}
                        onClick={() => removeSelectedBranch(selectedBranch.Id)}
                        data-testid="delete-chosen-branch"
                      />
                    </Tooltip>,
                  ]}
                >
                  <List.Item.Meta
                    title={generateSpanWithDataTestId(
                      selectedBranch.Name || '',
                      'name-chosen-branch'
                    )}
                    description={generateSpanWithDataTestId(
                      selectedBranch.Address || '',
                      'address-chosen-branch'
                    )}
                  />
                </List.Item>
              )}
            />
          ) : (
            <Typography.Text
              className="ant-form-text"
              type="secondary"
              data-testid="branches-not-chosen"
            >
              (Филиалы не указаны)
            </Typography.Text>
          )}
        </Form.Item>

        <Form.Item
          wrapperCol={{ offset: 0, span: 16 }}
          className="form-item-branches-button"
        >
          <Button
            htmlType="button"
            onClick={showUserModal}
            data-testid="add-branches"
          >
            Указать филиалы
          </Button>
        </Form.Item>
      </div>

      {required && <Typography.Text type="danger">{error}</Typography.Text>}

      <Modal
        title={
          <Typography.Title level={4} style={{ margin: '0' }}>
            {generateSpanWithDataTestId(
              'Укажите филиалы',
              'choose-branch-title'
            )}
          </Typography.Title>
        }
        open={open}
        onOk={onOk}
        onCancel={onCancel}
        closeIcon={<CloseIconsForModalWithDataTestId />}
        okText={'Сохранить'}
        cancelText={'Отменить'}
        cancelButtonProps={{
          'data-testid': 'cancel-add-branch',
        }}
        okButtonProps={{
          'data-testid': 'save-add-branch',
        }}
      >
        {serverError ? (
          <div>при загрузке филиалов произошла ошибка</div>
        ) : (
          <>
            <Input
              allowClear
              prefix={<SearchOutlined />}
              placeholder="Поиск по названию филиала"
              aria-label="Поиск по названию филиала"
              value={searchValue}
              onChange={searchChangeHandler}
              data-testid="search-branch"
            />
            <Form
              form={form}
              layout="vertical"
              name="selectBranchesForm"
              style={{ margin: '20px 0' }}
            >
              <List
                itemLayout="horizontal"
                dataSource={filteredBranches}
                locale={{
                  emptyText: isSearchApplied ? (
                    <Empty
                      description={
                        <span data-testid="branches-not-found">
                          Филиалы не найдены
                        </span>
                      }
                    />
                  ) : (
                    <EmptyComponentWithDataTestId />
                  ),
                }}
                renderItem={(branch) => (
                  <List.Item
                    key={branch.Id}
                    style={{ display: 'flex', alignItems: 'center' }}
                    data-testid="branch-item"
                  >
                    <Form.Item
                      style={{
                        marginRight: '10px',
                        marginBottom: '0',
                        alignSelf: 'center',
                      }}
                    >
                      {checkedBranches.find(
                        ({ Id: localId }) => localId === branch.Id
                      ) ? (
                        <Checkbox
                          checked={true}
                          onChange={(e: CheckboxChangeEvent) =>
                            onChange(e, branch)
                          }
                          data-testid="checkbox-choose-branch"
                        />
                      ) : (
                        <Checkbox
                          checked={false}
                          onChange={(e: CheckboxChangeEvent) =>
                            onChange(e, branch)
                          }
                          data-testid="checkbox-choose-branch"
                        />
                      )}
                    </Form.Item>
                    <List.Item.Meta
                      title={<div data-testid="name-branch">{branch.Name}</div>}
                      description={generateSpanWithDataTestId(
                        branch.Address || '',
                        'address-branch'
                      )}
                    />
                  </List.Item>
                )}
              />
            </Form>
          </>
        )}
      </Modal>
    </>
  );
};
